import { useState } from 'react';
import { format } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { dateFnsLocale } from '@shared/i18n/lib/date-locale';
import { cn } from '@shared/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

interface MonthPickerProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

const parseMonth = (value: string): { year: number; month: number } => {
  const [yearText, monthText] = value.split('-');
  const year = Number(yearText);
  const month = Number(monthText);
  const now = new Date();
  return {
    year: Number.isInteger(year) && year > 0 ? year : now.getFullYear(),
    month: month >= 1 && month <= 12 ? month : now.getMonth() + 1,
  };
};

export function MonthPicker({ id, value, onChange, className }: MonthPickerProps) {
  const { i18n } = useTranslation();
  const selected = parseMonth(value);
  const [year, setYear] = useState(selected.year);
  const [open, setOpen] = useState(false);
  const locale = dateFnsLocale(i18n.language);
  const label = format(new Date(selected.year, selected.month - 1, 1), 'LLLL yyyy', { locale });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          className={cn(
            'inline-flex h-10 w-full items-center rounded-lg border border-border bg-background px-3 text-left text-sm text-foreground',
            className,
          )}
        >
          <span className="truncate capitalize">{label}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64" align="start">
        <div className="mb-3 flex items-center justify-between">
          <button
            type="button"
            className="inline-flex size-8 items-center justify-center rounded-lg border border-border text-foreground hover:bg-accent"
            onClick={() => setYear((current) => current - 1)}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-sm font-medium">{year}</span>
          <button
            type="button"
            className="inline-flex size-8 items-center justify-center rounded-lg border border-border text-foreground hover:bg-accent"
            onClick={() => setYear((current) => current + 1)}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <div className="grid grid-cols-3 gap-1">
          {Array.from({ length: 12 }, (_, index) => {
            const month = index + 1;
            const active = year === selected.year && month === selected.month;
            return (
              <button
                key={month}
                type="button"
                className={cn(
                  'h-9 rounded-lg text-sm capitalize text-foreground hover:bg-accent',
                  active && 'bg-primary text-primary-foreground hover:bg-primary',
                )}
                onClick={() => {
                  onChange(`${year}-${String(month).padStart(2, '0')}`);
                  setOpen(false);
                }}
              >
                {format(new Date(year, index, 1), 'LLL', { locale })}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
